import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  writeBatch,
  where,
} from 'firebase/firestore';
import { db } from './firebase';
import { defaultTenantConfig } from '../config/tenant';
import { generateIncidentNumber } from '../utils/idGeneration';
import { enqueueOutboundDispatchSync } from './dispatchOutboundSyncService';
import {
  DispatchAssignment,
  DispatchEvent,
  DispatchEventType,
  DispatchFailoverFallbackChannel,
  DispatchFailoverMode,
  DispatchIncident,
  DispatchMessage,
  DispatchPriority,
  DispatchUnitSnapshot,
  ExemptionMode,
  IncidentOrigin,
  MessageSenderRole,
  ServiceLine,
  UnitLocationBroadcast,
  UnitStatus,
} from '../types/platform';

export interface DispatchBoardSnapshot {
  openIncidents: DispatchIncident[];
  pendingAssignments: DispatchAssignment[];
  availableUnits: DispatchUnitSnapshot[];
  allUnits: DispatchUnitSnapshot[];
  recentAssignments: DispatchAssignment[];
}

const ALLOWED_UNIT_TRANSITIONS: Record<UnitStatus, UnitStatus[]> = {
  AVAILABLE: ['ASSIGNED', 'ON_ROUTE_TO_STANDBY', 'AT_STANDBY', 'REFUELLING', 'VOR', 'OUT_OF_SERVICE'],
  ASSIGNED: ['MOBILE', 'AVAILABLE', 'OUT_OF_SERVICE', 'VOR'],
  MOBILE: ['ON_SCENE', 'AT_DESTINATION', 'ON_ROUTE_TO_STANDBY', 'AT_STANDBY', 'CLEAR', 'AVAILABLE', 'REFUELLING', 'VOR'],
  ON_ROUTE_TO_STANDBY: ['AT_STANDBY', 'AVAILABLE', 'ASSIGNED', 'REFUELLING', 'VOR', 'OUT_OF_SERVICE'],
  AT_STANDBY: ['ASSIGNED', 'MOBILE', 'AVAILABLE', 'REFUELLING', 'VOR', 'OUT_OF_SERVICE'],
  REFUELLING: ['AVAILABLE', 'AT_STANDBY', 'ASSIGNED', 'VOR', 'OUT_OF_SERVICE'],
  VOR: ['AVAILABLE', 'OUT_OF_SERVICE'],
  ON_SCENE: ['AT_DESTINATION', 'CLEAR', 'AVAILABLE', 'VOR'],
  AT_DESTINATION: ['CLEAR', 'AVAILABLE', 'VOR'],
  CLEAR: ['AVAILABLE', 'ON_ROUTE_TO_STANDBY', 'AT_STANDBY', 'REFUELLING', 'VOR', 'OUT_OF_SERVICE'],
  OUT_OF_SERVICE: ['AVAILABLE', 'VOR'],
};

export const isValidUnitStatusTransition = (from: UnitStatus, to: UnitStatus): boolean => {
  if (from === to) return true;
  const allowed = ALLOWED_UNIT_TRANSITIONS[from] || [];
  return allowed.includes(to);
};

const assertUnitTransition = (from: UnitStatus, to: UnitStatus, context: string): void => {
  if (!isValidUnitStatusTransition(from, to)) {
    throw new Error(`Invalid unit status transition (${from} -> ${to}) during ${context}.`);
  }
};

const assertUnitAssetReadiness = async (unit: DispatchUnitSnapshot): Promise<void> => {
  if (unit.fleetVehicleId) {
    const vehicleSnap = await getDoc(doc(db, 'fleet', unit.fleetVehicleId));
    if (!vehicleSnap.exists()) {
      throw new Error(`Linked vehicle ${unit.fleetVehicleId} not found.`);
    }
    const vehicleData = vehicleSnap.data() as any;
    if (vehicleData.tenantId && vehicleData.tenantId !== unit.tenantId) {
      throw new Error(`Linked vehicle ${unit.fleetVehicleId} belongs to a different tenant.`);
    }
    const vehicleStatus = (vehicleData.status || '').toString();
    if (vehicleStatus !== 'Operational') {
      throw new Error(`Linked vehicle ${unit.fleetVehicleId} is not operational (${vehicleStatus || 'Unknown'}).`);
    }
  }

  const linkedKits = unit.medicalKitIds || [];
  for (const kitId of linkedKits) {
    const kitSnap = await getDoc(doc(db, 'medical_kits', kitId));
    if (!kitSnap.exists()) {
      throw new Error(`Linked medical kit ${kitId} not found.`);
    }
    const kitData = kitSnap.data() as any;
    if (kitData.tenantId && kitData.tenantId !== unit.tenantId) {
      throw new Error(`Linked medical kit ${kitId} belongs to a different tenant.`);
    }
    const kitStatus = (kitData.status || '').toString();
    if (kitStatus !== 'Ready') {
      throw new Error(`Linked medical kit ${kitId} is not ready (${kitStatus || 'Unknown'}).`);
    }
  }
};

const withId = <T extends object>(id: string, data: object): T => {
  return { id, ...(data as T) };
};

const buildDispatchEvent = (input: {
  tenantId: string;
  eventType: DispatchEventType;
  message: string;
  incidentId?: string;
  assignmentId?: string;
  unitId?: string;
  actorName?: string;
  actorRole?: string;
}) => {
  return {
    tenantId: input.tenantId,
    eventType: input.eventType,
    message: input.message,
    incidentId: input.incidentId,
    assignmentId: input.assignmentId,
    unitId: input.unitId,
    actorName: input.actorName,
    actorRole: input.actorRole,
    createdAt: new Date().toISOString(),
    createdAtServer: serverTimestamp(),
  };
};

const queueOutboundDispatchUpdate = async (input: {
  tenantId: string;
  eventType: DispatchEventType;
  payload: Record<string, unknown>;
  actorName?: string;
  actorRole?: string;
}): Promise<void> => {
  try {
    await enqueueOutboundDispatchSync({
      tenantId: input.tenantId,
      eventType: input.eventType,
      payload: input.payload,
      actorName: input.actorName,
      actorRole: input.actorRole,
      requestKey: `${input.eventType}:${input.tenantId}:${Date.now()}`,
      config: {
        enabled: defaultTenantConfig.dispatchIntegration.outboundSyncEnabled === true,
        endpoint: defaultTenantConfig.dispatchIntegration.outboundSyncEndpoint,
        authToken: defaultTenantConfig.dispatchIntegration.outboundSyncAuthToken,
        retrySeconds: defaultTenantConfig.dispatchIntegration.outboundSyncRetrySeconds || 30,
      },
    });
  } catch (error) {
    console.error('Outbound dispatch sync enqueue failed', error);
  }
};

export const subscribeDispatchBoard = (
  tenantId: string,
  onData: (snapshot: DispatchBoardSnapshot) => void,
  onError?: (error: Error) => void
): (() => void) => {
  const state: DispatchBoardSnapshot = {
    openIncidents: [],
    pendingAssignments: [],
    availableUnits: [],
    allUnits: [],
    recentAssignments: [],
  };

  const emit = () => onData({ ...state });

  const incidentQuery = query(
    collection(db, 'dispatch_incidents'),
    where('tenantId', '==', tenantId),
    where('status', 'in', ['OPEN', 'DISPATCHED']),
    orderBy('createdAt', 'asc')
  );

  const assignmentQuery = query(
    collection(db, 'dispatch_assignments'),
    where('tenantId', '==', tenantId),
    where('status', '==', 'PENDING_ACK')
  );

  const unitQuery = query(
    collection(db, 'dispatch_units'),
    where('tenantId', '==', tenantId),
    where('status', 'in', ['AVAILABLE', 'AT_STANDBY'])
  );

  const allUnitsQuery = query(
    collection(db, 'dispatch_units'),
    where('tenantId', '==', tenantId)
  );

  const recentAssignmentsQuery = query(
    collection(db, 'dispatch_assignments'),
    where('tenantId', '==', tenantId),
    orderBy('assignedAt', 'desc'),
    limit(50)
  );

  const unsubIncidents = onSnapshot(
    incidentQuery,
    (snap) => {
      state.openIncidents = snap.docs.map((docSnap) => withId<DispatchIncident>(docSnap.id, docSnap.data()));
      emit();
    },
    (error) => onError?.(error)
  );

  const unsubAssignments = onSnapshot(
    assignmentQuery,
    (snap) => {
      state.pendingAssignments = snap.docs.map((docSnap) => withId<DispatchAssignment>(docSnap.id, docSnap.data()));
      emit();
    },
    (error) => onError?.(error)
  );

  const unsubUnits = onSnapshot(
    unitQuery,
    (snap) => {
      state.availableUnits = snap.docs.map((docSnap) => withId<DispatchUnitSnapshot>(docSnap.id, docSnap.data()));
      emit();
    },
    (error) => onError?.(error)
  );

  const unsubAllUnits = onSnapshot(
    allUnitsQuery,
    (snap) => {
      state.allUnits = snap.docs.map((docSnap) => withId<DispatchUnitSnapshot>(docSnap.id, docSnap.data()));
      emit();
    },
    (error) => onError?.(error)
  );

  const unsubRecentAssignments = onSnapshot(
    recentAssignmentsQuery,
    (snap) => {
      state.recentAssignments = snap.docs.map((docSnap) => withId<DispatchAssignment>(docSnap.id, docSnap.data()));
      emit();
    },
    (error) => onError?.(error)
  );

  return () => {
    unsubIncidents();
    unsubAssignments();
    unsubUnits();
    unsubAllUnits();
    unsubRecentAssignments();
  };
};

export const createDispatchIncident = async (input: {
  tenantId: string;
  incidentPrefix?: string;
  identifierPolicy?: {
    incidentPrefix?: string;
    incidentPattern?: string;
  };
  priority: DispatchPriority;
  category: string;
  locationText: string;
  origin: IncidentOrigin;
  serviceLine: ServiceLine;
  clinicalSupportRequired: boolean;
  linkedEprfId?: string;
  actorName?: string;
  actorRole?: string;
}): Promise<{ incidentId: string; incidentNumber: string }> => {
  const incidentNumber = generateIncidentNumber({
    incidentPrefix: input.incidentPrefix || defaultTenantConfig.incidentPrefix,
    identifierPolicy: {
      ...(defaultTenantConfig.identifierPolicy || {}),
      ...(input.identifierPolicy || {}),
      ...(input.incidentPrefix ? { incidentPrefix: input.incidentPrefix } : {}),
    },
  });

  const incidentRef = doc(collection(db, 'dispatch_incidents'));
  const eventRef = doc(collection(db, 'dispatch_events'));

  const batch = writeBatch(db);
  batch.set(incidentRef, {
    tenantId: input.tenantId,
    incidentNumber,
    priority: input.priority,
    status: 'OPEN',
    category: input.category,
    locationText: input.locationText,
    origin: input.origin,
    serviceLine: input.serviceLine,
    clinicalSupportRequired: input.clinicalSupportRequired,
    linkedEprfId: input.linkedEprfId || null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdAtServer: serverTimestamp(),
    updatedAtServer: serverTimestamp(),
  });
  batch.set(
    eventRef,
    buildDispatchEvent({
      tenantId: input.tenantId,
      incidentId: incidentRef.id,
      eventType: 'INCIDENT_CREATED',
      message: `Incident created (${input.priority}) at ${input.locationText}`,
      actorName: input.actorName,
      actorRole: input.actorRole,
    })
  );
  await batch.commit();

  return {
    incidentId: incidentRef.id,
    incidentNumber,
  };
};

export const createDispatchUnit = async (input: {
  tenantId: string;
  callSign: string;
  serviceLines: ServiceLine[];
  assignedUserIds?: string[];
  fleetVehicleId?: string;
  medicalKitIds?: string[];
  status?: UnitStatus;
  actorName?: string;
  actorRole?: string;
}): Promise<void> => {
  const nowIso = new Date().toISOString();
  const unitRef = doc(collection(db, 'dispatch_units'));
  const eventRef = doc(collection(db, 'dispatch_events'));

  const batch = writeBatch(db);
  batch.set(unitRef, {
    tenantId: input.tenantId,
    callSign: input.callSign,
    serviceLines: input.serviceLines,
    assignedUserIds: input.assignedUserIds || [],
    fleetVehicleId: input.fleetVehicleId || null,
    medicalKitIds: input.medicalKitIds || [],
    status: input.status || 'AVAILABLE',
    lastUpdatedAt: nowIso,
    createdAtServer: serverTimestamp(),
    updatedAtServer: serverTimestamp(),
  });
  batch.set(
    eventRef,
    buildDispatchEvent({
      tenantId: input.tenantId,
      unitId: unitRef.id,
      eventType: 'UNIT_REGISTERED',
      message: `Unit ${input.callSign} registered`,
      actorName: input.actorName,
      actorRole: input.actorRole,
    })
  );
  await batch.commit();
};

export const ensureCrewDispatchUnit = async (input: {
  tenantId: string;
  callSign: string;
  crewUserId: string;
  serviceLines: ServiceLine[];
  fleetVehicleId?: string;
  medicalKitIds?: string[];
  isGhostUnit?: boolean;
  actorName?: string;
  actorRole?: string;
}): Promise<{ unitId: string; created: boolean }> => {
  const nowIso = new Date().toISOString();
  const existingQuery = query(
    collection(db, 'dispatch_units'),
    where('tenantId', '==', input.tenantId),
    where('callSign', '==', input.callSign),
    limit(1)
  );
  const existingSnap = await getDocs(existingQuery);

  if (existingSnap.empty) {
    const unitRef = doc(collection(db, 'dispatch_units'));
    const eventRef = doc(collection(db, 'dispatch_events'));
    const batch = writeBatch(db);

    batch.set(unitRef, {
      tenantId: input.tenantId,
      callSign: input.callSign,
      serviceLines: input.serviceLines,
      assignedUserIds: [input.crewUserId],
      fleetVehicleId: input.fleetVehicleId || null,
      medicalKitIds: input.medicalKitIds || [],
      isGhostUnit: Boolean(input.isGhostUnit),
      status: 'AVAILABLE',
      lastUpdatedAt: nowIso,
      createdAtServer: serverTimestamp(),
      updatedAtServer: serverTimestamp(),
    });

    batch.set(
      eventRef,
      buildDispatchEvent({
        tenantId: input.tenantId,
        unitId: unitRef.id,
        eventType: 'UNIT_REGISTERED',
        message: `Unit ${input.callSign} auto-registered from crew sign-on`,
        actorName: input.actorName,
        actorRole: input.actorRole,
      })
    );

    await batch.commit();
    return { unitId: unitRef.id, created: true };
  }

  const existingDoc = existingSnap.docs[0];
  const existingData = existingDoc.data() as DispatchUnitSnapshot;
  const mergedAssignedUsers = Array.from(new Set([...(existingData.assignedUserIds || []), input.crewUserId]));
  const mergedServiceLines = Array.from(new Set([...(existingData.serviceLines || []), ...input.serviceLines]));
  const mergedMedicalKits = Array.from(new Set([...(existingData.medicalKitIds || []), ...(input.medicalKitIds || [])]));

  const updatePayload: Record<string, unknown> = {
    assignedUserIds: mergedAssignedUsers,
    serviceLines: mergedServiceLines,
    medicalKitIds: mergedMedicalKits,
    isGhostUnit: Boolean(input.isGhostUnit),
    lastUpdatedAt: nowIso,
    updatedAtServer: serverTimestamp(),
  };

  if (input.fleetVehicleId) {
    updatePayload.fleetVehicleId = input.fleetVehicleId;
  }

  await setDoc(doc(db, 'dispatch_units', existingDoc.id), updatePayload, { merge: true });
  return { unitId: existingDoc.id, created: false };
};

export const assignUnitToIncident = async (input: {
  tenantId: string;
  incidentId: string;
  unitId: string;
  sourceAdapter: 'NATIVE' | 'GOODSAM' | 'HYBRID' | 'EXTERNAL_API';
  actorName?: string;
  actorRole?: string;
}): Promise<void> => {
  const nowIso = new Date().toISOString();
  const incidentRef = doc(db, 'dispatch_incidents', input.incidentId);
  const unitRef = doc(db, 'dispatch_units', input.unitId);
  const incidentSnap = await getDoc(incidentRef);
  const unitSnap = await getDoc(unitRef);

  if (!incidentSnap.exists()) {
    throw new Error('Incident not found.');
  }
  if (!unitSnap.exists()) {
    throw new Error('Unit not found.');
  }

  const incidentData = incidentSnap.data() as DispatchIncident;
  const unitData = unitSnap.data() as DispatchUnitSnapshot;

  assertUnitTransition(unitData.status, 'ASSIGNED', 'assignment');
  await assertUnitAssetReadiness(unitData);

  const assignmentRef = doc(collection(db, 'dispatch_assignments'));
  const eventRef = doc(collection(db, 'dispatch_events'));

  const batch = writeBatch(db);
  batch.set(assignmentRef, {
    tenantId: input.tenantId,
    incidentId: input.incidentId,
    incidentNumber: incidentData.incidentNumber,
    unitId: input.unitId,
    unitCallSign: unitData.callSign,
    assignedUserIds: unitData.assignedUserIds || [],
    sourceAdapter: input.sourceAdapter,
    assignedAt: nowIso,
    status: 'PENDING_ACK',
    createdAtServer: serverTimestamp(),
    updatedAtServer: serverTimestamp(),
  });
  batch.update(incidentRef, {
    status: 'DISPATCHED',
    updatedAt: nowIso,
    updatedAtServer: serverTimestamp(),
  });
  batch.update(unitRef, {
    status: 'ASSIGNED',
    lastUpdatedAt: nowIso,
    updatedAtServer: serverTimestamp(),
  });
  batch.set(
    eventRef,
    buildDispatchEvent({
      tenantId: input.tenantId,
      incidentId: input.incidentId,
      assignmentId: assignmentRef.id,
      unitId: input.unitId,
      eventType: 'ASSIGNMENT_SENT',
      message: `Assignment sent to unit ${input.unitId} via ${input.sourceAdapter}`,
      actorName: input.actorName,
      actorRole: input.actorRole,
    })
  );
  await batch.commit();

  await queueOutboundDispatchUpdate({
    tenantId: input.tenantId,
    eventType: 'ASSIGNMENT_SENT',
    payload: {
      incidentId: input.incidentId,
      assignmentId: assignmentRef.id,
      unitId: input.unitId,
      sourceAdapter: input.sourceAdapter,
      status: 'PENDING_ACK',
    },
    actorName: input.actorName,
    actorRole: input.actorRole,
  });
};

export const acknowledgeAssignment = async (input: {
  tenantId: string;
  incidentId: string;
  assignmentId: string;
  unitId: string;
  actorName?: string;
  actorRole?: string;
}): Promise<void> => {
  const nowIso = new Date().toISOString();
  const assignmentRef = doc(db, 'dispatch_assignments', input.assignmentId);
  const unitRef = doc(db, 'dispatch_units', input.unitId);
  const eventRef = doc(collection(db, 'dispatch_events'));

  const unitSnap = await getDoc(unitRef);
  if (!unitSnap.exists()) {
    throw new Error('Unit not found.');
  }
  const unitData = unitSnap.data() as DispatchUnitSnapshot;
  assertUnitTransition(unitData.status, 'MOBILE', 'assignment acknowledgement');

  const batch = writeBatch(db);
  batch.update(assignmentRef, {
    status: 'ACKNOWLEDGED',
    acknowledgedAt: nowIso,
    updatedAtServer: serverTimestamp(),
  });
  batch.update(unitRef, {
    status: 'MOBILE',
    lastUpdatedAt: nowIso,
    updatedAtServer: serverTimestamp(),
  });
  batch.set(
    eventRef,
    buildDispatchEvent({
      tenantId: input.tenantId,
      incidentId: input.incidentId,
      assignmentId: input.assignmentId,
      unitId: input.unitId,
      eventType: 'ASSIGNMENT_ACKNOWLEDGED',
      message: `Unit ${input.unitId} acknowledged assignment`,
      actorName: input.actorName,
      actorRole: input.actorRole,
    })
  );
  await batch.commit();

  await queueOutboundDispatchUpdate({
    tenantId: input.tenantId,
    eventType: 'ASSIGNMENT_ACKNOWLEDGED',
    payload: {
      incidentId: input.incidentId,
      assignmentId: input.assignmentId,
      unitId: input.unitId,
      unitStatus: 'MOBILE',
      assignmentStatus: 'ACKNOWLEDGED',
      acknowledgedAt: nowIso,
    },
    actorName: input.actorName,
    actorRole: input.actorRole,
  });
};

export const declineAssignment = async (input: {
  tenantId: string;
  assignmentId: string;
  incidentId: string;
  unitId: string;
  actorName?: string;
  actorRole?: string;
}): Promise<void> => {
  const nowIso = new Date().toISOString();
  const assignmentRef = doc(db, 'dispatch_assignments', input.assignmentId);
  const incidentRef = doc(db, 'dispatch_incidents', input.incidentId);
  const unitRef = doc(db, 'dispatch_units', input.unitId);
  const eventRef = doc(collection(db, 'dispatch_events'));

  const unitSnap = await getDoc(unitRef);
  if (!unitSnap.exists()) {
    throw new Error('Unit not found.');
  }
  const unitData = unitSnap.data() as DispatchUnitSnapshot;
  assertUnitTransition(unitData.status, 'AVAILABLE', 'assignment decline');

  const batch = writeBatch(db);
  batch.update(assignmentRef, {
    status: 'DECLINED',
    updatedAtServer: serverTimestamp(),
  });
  batch.update(incidentRef, {
    status: 'OPEN',
    updatedAt: nowIso,
    updatedAtServer: serverTimestamp(),
  });
  batch.update(unitRef, {
    status: 'AVAILABLE',
    lastUpdatedAt: nowIso,
    updatedAtServer: serverTimestamp(),
  });
  batch.set(
    eventRef,
    buildDispatchEvent({
      tenantId: input.tenantId,
      incidentId: input.incidentId,
      assignmentId: input.assignmentId,
      unitId: input.unitId,
      eventType: 'ASSIGNMENT_DECLINED',
      message: `Unit ${input.unitId} declined assignment; incident reopened`,
      actorName: input.actorName,
      actorRole: input.actorRole,
    })
  );
  await batch.commit();

  await queueOutboundDispatchUpdate({
    tenantId: input.tenantId,
    eventType: 'ASSIGNMENT_DECLINED',
    payload: {
      incidentId: input.incidentId,
      assignmentId: input.assignmentId,
      unitId: input.unitId,
      incidentStatus: 'OPEN',
      unitStatus: 'AVAILABLE',
      assignmentStatus: 'DECLINED',
    },
    actorName: input.actorName,
    actorRole: input.actorRole,
  });
};

export const closeIncident = async (input: {
  tenantId: string;
  incidentId: string;
  unitId?: string;
  closureReason?: string;
  actorName?: string;
  actorRole?: string;
}): Promise<void> => {
  const nowIso = new Date().toISOString();
  const incidentRef = doc(db, 'dispatch_incidents', input.incidentId);
  const eventRef = doc(collection(db, 'dispatch_events'));

  const batch = writeBatch(db);
  batch.update(incidentRef, {
    status: 'CLOSED',
    updatedAt: nowIso,
    updatedAtServer: serverTimestamp(),
  });

  if (input.unitId) {
    const unitRef = doc(db, 'dispatch_units', input.unitId);
    const unitSnap = await getDoc(unitRef);
    if (!unitSnap.exists()) {
      throw new Error('Unit not found.');
    }
    const unitData = unitSnap.data() as DispatchUnitSnapshot;
    assertUnitTransition(unitData.status, 'AVAILABLE', 'incident close');

    batch.update(doc(db, 'dispatch_units', input.unitId), {
      status: 'AVAILABLE',
      lastUpdatedAt: nowIso,
      updatedAtServer: serverTimestamp(),
    });
  }

  batch.set(
    eventRef,
    buildDispatchEvent({
      tenantId: input.tenantId,
      incidentId: input.incidentId,
      unitId: input.unitId,
      eventType: 'INCIDENT_CLOSED',
      message: input.closureReason
        ? `Incident ${input.incidentId} closed (${input.closureReason})`
        : `Incident ${input.incidentId} closed`,
      actorName: input.actorName,
      actorRole: input.actorRole,
    })
  );

  await batch.commit();

  await queueOutboundDispatchUpdate({
    tenantId: input.tenantId,
    eventType: 'INCIDENT_CLOSED',
    payload: {
      incidentId: input.incidentId,
      unitId: input.unitId || null,
      incidentStatus: 'CLOSED',
      unitStatus: input.unitId ? 'AVAILABLE' : null,
      closureReason: input.closureReason || null,
      closedAt: nowIso,
    },
    actorName: input.actorName,
    actorRole: input.actorRole,
  });
};

export const subscribeDispatchEvents = (
  tenantId: string,
  incidentId: string,
  onData: (events: DispatchEvent[]) => void,
  onError?: (error: Error) => void
): (() => void) => {
  const eventQuery = query(
    collection(db, 'dispatch_events'),
    where('tenantId', '==', tenantId),
    where('incidentId', '==', incidentId),
    orderBy('createdAt', 'asc')
  );

  return onSnapshot(
    eventQuery,
    (snap) => {
      onData(snap.docs.map((docSnap) => withId<DispatchEvent>(docSnap.id, docSnap.data())));
    },
    (error) => onError?.(error)
  );
};

// ── Crew-initiated unit status update ────────────────────────────────────────

export const updateUnitStatus = async (input: {
  tenantId: string;
  unitId: string;
  userId: string;
  actorName?: string;
  actorRole?: string;
  incidentId?: string;
  assignmentId?: string;
  newStatus: UnitStatus;
  operationLabel?: string;
}): Promise<void> => {
  const nowIso = new Date().toISOString();
  const unitRef = doc(db, 'dispatch_units', input.unitId);
  const unitSnap = await getDoc(unitRef);

  if (!unitSnap.exists()) throw new Error(`Unit ${input.unitId} not found.`);

  const unitData = unitSnap.data() as DispatchUnitSnapshot;
  assertUnitTransition(unitData.status, input.newStatus, 'crew status update');

  const eventRef = doc(collection(db, 'dispatch_events'));
  const batch = writeBatch(db);

  batch.update(unitRef, {
    status: input.newStatus,
    lastUpdatedAt: nowIso,
    updatedAtServer: serverTimestamp(),
  });

  batch.set(eventRef, buildDispatchEvent({
    tenantId: input.tenantId,
    incidentId: input.incidentId,
    assignmentId: input.assignmentId,
    unitId: input.unitId,
    eventType: 'UNIT_STATUS_UPDATED',
    message: input.operationLabel
      ? `Unit ${input.unitId} → ${input.newStatus} (${input.operationLabel})`
      : `Unit ${input.unitId} → ${input.newStatus}`,
    actorName: input.actorName,
    actorRole: input.actorRole,
  }));

  await batch.commit();

  await queueOutboundDispatchUpdate({
    tenantId: input.tenantId,
    eventType: 'UNIT_STATUS_UPDATED',
    payload: {
      incidentId: input.incidentId || null,
      assignmentId: input.assignmentId || null,
      unitId: input.unitId,
      unitStatus: input.newStatus,
      operationLabel: input.operationLabel || null,
      updatedAt: nowIso,
    },
    actorName: input.actorName,
    actorRole: input.actorRole,
  });
};

// ── Exemption mode audit log ─────────────────────────────────────────────────

export const logExemptionModeChange = async (input: {
  tenantId: string;
  unitId: string;
  actorName?: string;
  actorRole?: string;
  incidentId?: string;
  assignmentId?: string;
  newMode: ExemptionMode;
}): Promise<void> => {
  const eventRef = doc(collection(db, 'dispatch_events'));
  const state = input.newMode === 'active' ? 'ACTIVATED' : 'DEACTIVATED';
  await setDoc(eventRef, buildDispatchEvent({
    tenantId: input.tenantId,
    incidentId: input.incidentId,
    assignmentId: input.assignmentId,
    unitId: input.unitId,
    eventType: 'EXEMPTION_MODE_CHANGED',
    message: `Exemption mode ${state} on unit ${input.unitId}`,
    actorName: input.actorName,
    actorRole: input.actorRole,
  }));
};

// ── Integration failover mode (manual/phone/radio SOP) ─────────────────────

export const subscribeDispatchFailoverMode = (
  tenantId: string,
  onData: (mode: DispatchFailoverMode) => void,
  onError?: (error: Error) => void
): (() => void) => {
  const failoverRef = doc(db, 'dispatch_failover_modes', tenantId);

  return onSnapshot(
    failoverRef,
    (snap) => {
      const nowIso = new Date().toISOString();
      if (!snap.exists()) {
        onData({
          tenantId,
          active: false,
          primaryFallbackChannel: 'MANUAL',
          sopAcknowledged: false,
          updatedAt: nowIso,
        });
        return;
      }
      onData(withId<DispatchFailoverMode>(snap.id, snap.data()));
    },
    (error) => onError?.(error)
  );
};

export const setDispatchFailoverMode = async (input: {
  tenantId: string;
  active: boolean;
  primaryFallbackChannel: DispatchFailoverFallbackChannel;
  reason?: string;
  sopAcknowledged: boolean;
  actorName?: string;
  actorRole?: string;
}): Promise<void> => {
  if (input.active && !input.sopAcknowledged) {
    throw new Error('SOP acknowledgement is required before activating failover mode.');
  }

  const nowIso = new Date().toISOString();
  const failoverRef = doc(db, 'dispatch_failover_modes', input.tenantId);
  const eventRef = doc(collection(db, 'dispatch_events'));

  const currentSnap = await getDoc(failoverRef);
  const current = currentSnap.exists() ? (currentSnap.data() as Partial<DispatchFailoverMode>) : null;

  const payload: Partial<DispatchFailoverMode> = {
    tenantId: input.tenantId,
    active: input.active,
    primaryFallbackChannel: input.primaryFallbackChannel,
    reason: input.reason?.trim() || undefined,
    sopAcknowledged: input.sopAcknowledged,
    acknowledgedByName: input.sopAcknowledged ? input.actorName : undefined,
    acknowledgedByRole: input.sopAcknowledged ? input.actorRole : undefined,
    updatedAt: nowIso,
  };

  if (input.active && !current?.active) {
    payload.activatedAt = nowIso;
    payload.resolvedAt = undefined;
  }
  if (!input.active && current?.active) {
    payload.resolvedAt = nowIso;
  }

  const batch = writeBatch(db);
  batch.set(failoverRef, {
    ...payload,
    updatedAtServer: serverTimestamp(),
  }, { merge: true });
  batch.set(
    eventRef,
    buildDispatchEvent({
      tenantId: input.tenantId,
      eventType: 'INTEGRATION_FAILOVER_MODE_CHANGED',
      message: input.active
        ? `Integration failover activated (${input.primaryFallbackChannel})${input.reason ? `: ${input.reason}` : ''}`
        : `Integration failover cleared${input.reason ? `: ${input.reason}` : ''}`,
      actorName: input.actorName,
      actorRole: input.actorRole,
    })
  );

  await batch.commit();
};

// ── Unit location broadcasting (dispatcher live map) ──────────────────────────

export const subscribeUnitLocations = (
  tenantId: string,
  onUpdate: (locations: UnitLocationBroadcast[]) => void,
  onError?: (error: Error) => void
): (() => void) => {
  const q = query(
    collection(db, 'dispatch_unit_locations'),
    where('tenantId', '==', tenantId)
  );

  return onSnapshot(
    q,
    (snap) => {
      onUpdate(snap.docs.map((d) => withId<UnitLocationBroadcast>(d.id, d.data())));
    },
    (error) => onError?.(error)
  );
};

// ── Dispatch messaging ────────────────────────────────────────────────────────

export const sendDispatchMessage = async (input: {
  tenantId: string;
  assignmentId: string;
  senderId: string;
  senderName: string;
  senderRole: MessageSenderRole;
  body: string;
  quickMessageKey?: string;
}): Promise<void> => {
  const msgRef = doc(collection(db, 'dispatch_messages', input.assignmentId, 'messages'));
  const now = new Date().toISOString();

  await setDoc(msgRef, {
    assignmentId: input.assignmentId,
    tenantId: input.tenantId,
    senderId: input.senderId,
    senderName: input.senderName,
    senderRole: input.senderRole,
    body: input.body,
    quickMessageKey: input.quickMessageKey ?? null,
    readByDispatch: input.senderRole === 'dispatch',
    readByCrew: input.senderRole === 'crew',
    sentAt: now,
    sentAtServer: serverTimestamp(),
  });
};

export const subscribeAssignmentMessages = (
  assignmentId: string,
  onMessages: (messages: DispatchMessage[]) => void,
  onError?: (error: Error) => void
): (() => void) => {
  const q = query(
    collection(db, 'dispatch_messages', assignmentId, 'messages'),
    orderBy('sentAt', 'asc')
  );

  return onSnapshot(
    q,
    (snap) => {
      onMessages(snap.docs.map((d) => withId<DispatchMessage>(d.id, d.data())));
    },
    (error) => onError?.(error)
  );
};

// ── Incident location coord update (dispatcher map-based edit) ────────────────

export const updateIncidentCoords = async (
  incidentId: string,
  coords: [number, number]  // [lat, lng]
): Promise<void> => {
  const incidentRef = doc(db, 'dispatch_incidents', incidentId);
  await setDoc(
    incidentRef,
    { locationCoords: coords, updatedAt: new Date().toISOString(), updatedAtServer: serverTimestamp() },
    { merge: true }
  );
};
